import { Injectable, signal } from "@angular/core";

/** A single live worker reasoning step, keyed by source+task_id+step_label. */
export interface WorkerReasoningStep {
  /** Stable identity: `${source}|${task_id}|${step_label}`. Updated in place. */
  key: string;
  /** Short header for the step, e.g. "Planning data query". */
  stepLabel: string;
  /** The sub-agent's chain-of-thought text so far. */
  reasoning: string;
  /** "in_progress" while streaming, "completed" once the step is done. */
  status: string;
}

/** Raw `reasoning` custom-event payload sent by the backend. */
export interface WorkerReasoningEvent {
  is_reasoning?: boolean;
  source?: string;
  task_id?: string | null;
  step_label?: string;
  reasoning?: string;
  status?: string;
}

/**
 * Holds sub-agent (e.g. mcp_assistant) chain-of-thought streamed as
 * `reasoning` SSE custom events.
 *
 * Sub-agents run isolated via `ainvoke`, so their planner reasoning never reaches
 * the parent `messages` stream the way a top-level node's does. The backend
 * re-emits it as a `reasoning` custom event (same path as
 * `tool_data_stream`); `chat-container` routes it here, and `chat-message-list`
 * weaves the stored steps into the current turn as reasoning items.
 *
 * Deltas of the same step share `source`+`task_id`+`step_label`, so successive
 * events (status="in_progress" carrying the growing text, then "completed")
 * update the SAME step in place — the UI renders it progressively, token by token.
 *
 * Provided per chat-container (NOT root) so it resets with the container. Cleared
 * explicitly on submit for new turns in the same conversation; do NOT clear via a
 * threadId effect — that races with the mid-run threadId transition and wipes the
 * live stream.
 */
@Injectable()
export class WorkerReasoningStreamService {
  readonly #steps = signal<WorkerReasoningStep[]>([]);

  /** Live reasoning steps in arrival order. Reactive — read in computed()s. */
  readonly steps = this.#steps.asReadonly();

  /** Upsert a step from a `reasoning` event (creates or updates in place). */
  store(event: WorkerReasoningEvent): void {
    const key = `${event.source ?? ""}|${event.task_id ?? ""}|${event.step_label ?? ""}`;
    const step: WorkerReasoningStep = {
      key,
      stepLabel: event.step_label || "Reasoning…",
      reasoning: event.reasoning ?? "",
      status: event.status ?? "completed",
    };
    this.#steps.update((steps) => {
      const idx = steps.findIndex((s) => s.key === key);
      if (idx === -1) return [...steps, step];
      const next = [...steps];
      next[idx] = step;
      return next;
    });
  }

  /** Drop all steps. Call on submit so a new turn starts clean. */
  clear(): void {
    this.#steps.set([]);
  }
}
